import React, { useEffect, useState, useMemo, useRef } from 'react'
import { BasePage as CngBasePage } from 'cng-web-lib'
import MpciApiClient from '../Common/services/MpciApiClient'
import DashboardService from './DashboardService'
import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useBreadcrumbNameMap from 'src/useBreadcrumbNameMap'
import MetricsCard from './MetricsCard'
import AnnouncementBanner from './AnnouncementBanner'
import FilingsByStatusChart from './FilingsByStatusChart'
import BILL_STATUSES from '../Common/constants/billStatuses'
import { getChartColors } from '../Common/constants/colors'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import useOrgFilter from '../Common/hooks/useOrgFilter'

// Maps status key → { label, countField, trendField, statusKey }
const KPI_CONFIG = [
  {
    key:        'SUBM',
    label:      'Submitted to NAIC',
    countField: 'subm30DaysCount',
    trendField: 'submWeeklyDiffCount',
    statusKey:  BILL_STATUSES.SUBMITTED_TO_NAIC,
    description: 'Filings sent to NAIC and awaiting a response.'
  },
  {
    key:        'ACT',
    label:      'Assessment Complete',
    countField: 'act30DaysCount',
    trendField: 'actWeeklyDiffCount',
    statusKey:  BILL_STATUSES.ASSESSMENT_COMPLETE,
    description: 'Shipment approved for transport to the UAE.'
  },
  {
    key:        'RFI',
    label:      'Pending Action (RFI)',
    countField: 'rfi30DaysCount',
    trendField: 'rfiWeeklyDiffCount',
    statusKey:  BILL_STATUSES.REQUEST_FOR_INFO,
    description: 'Awaiting more information or a document before assessment can continue.'
  },
  {
    key:        'DNL',
    label:      'Do Not Load',
    countField: 'dnl30DaysCount',
    trendField: 'dnlWeeklyDiffCount',
    statusKey:  BILL_STATUSES.DO_NOT_LOAD,
    description: 'Shipment is not allowed for transport to the UAE.'
  },
  {
    key:        'CONTRL_NACK',
    label:      'Failed Acknowledgement',
    countField: 'contrlNack30DaysCount',
    trendField: 'contrlNackWeeklyDiffCount',
    statusKey:  BILL_STATUSES.FAILED_ACK,
    description: 'Failed NAIC validation checks.'
  },
  {
    key:        'SUBM_ERR',
    label:      'Error in Submission',
    countField: 'submErr30DaysCount',
    trendField: 'submErrWeeklyDiffCount',
    statusKey:  BILL_STATUSES.ERROR_IN_SUBMISSION,
    description: 'Failed validation before reaching NAIC - not yet submitted.'
  }
]

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  }
}))

function DashboardContent({ showNotification }) {
  const classes = useStyles()
  const theme = useTheme()
    const [metrics, setMetrics] = useState([])
  const [kpiLoading, setKpiLoading] = useState(true)
  const [announcement, setAnnouncement] = useState(null)
  const mountedRef = useRef(true)

  // Org filter — shared with FilingsByStatusChart below (single control, lives inside
  // that card). Admin/parent-corp get a multiselect there; plain org gets no control at
  // all. Without a selection, resolvedOrgIds below defaults to own + child + grandchild
  // orgs (non-admin) or omits the filter entirely (admin sees all) — same client-computed
  // default as MasterBill/HouseBill/VesselMaster/PartyMaster list pages. The BE's own
  // fallback (when no orgId is sent at all) only resolves to the caller's bare own org,
  // so that default must be computed and sent explicitly here, not left to the BE.
  const { isAdmin, canActAsOtherOrg, orgList, defaultOrgIds, orgFilterLoading } = useOrgFilter()
  const [orgIds, setOrgIds] = useState([])
  const allowedOrgIds = orgList.map((o) => o.id)

  // Resolve chart colors from theme
  const chartColors = useMemo(() => getChartColors(theme), [theme])

  const fetchKpi = () => {
    setKpiLoading(true)
    const picked = orgIds.length > 0 ? orgIds : null
    const clampedPicked = picked && allowedOrgIds.length > 0
      ? picked.filter((id) => allowedOrgIds.includes(id))
      : picked
    const resolvedOrgIds = isAdmin
      ? (clampedPicked && clampedPicked.length > 0 ? clampedPicked : null)
      : (clampedPicked && clampedPicked.length > 0 ? clampedPicked : defaultOrgIds)
    const params = new URLSearchParams()
    ;(resolvedOrgIds ?? []).forEach((id) => params.append('orgId', id))
    const url = `${UaeMpciApiUrls.DASHBOARD_KPI}${params.toString() ? '?' + params.toString() : ''}`
    MpciApiClient.get(
      url,
      (data) => {
        if (!mountedRef.current) return
        // API: flat object { subm30DaysCount, act30DaysCount, submWeeklyDiffCount, ... }
                const mapped = KPI_CONFIG.map((cfg) => ({
          label:     cfg.label,
          colorKey:  cfg.key,        // BE status code — used to look up chart color
          statusKey: cfg.statusKey,
          count:     data?.[cfg.countField] ?? 0,
          trend:     data?.[cfg.trendField] ?? 0,
          description: cfg.description
        }))
        setMetrics(mapped)
        setKpiLoading(false)
      },
      (error) => {
        if (!mountedRef.current) return
        console.error('[Dashboard] KPI fetch failed', error)
        setKpiLoading(false)
      }
    )
  }

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Skip while org scope is still resolving — defaultOrgIds is only the narrow
  // [loggedInOrgId] placeholder until then, and it can tick through intermediate/partial
  // states as the async child-org resolution progresses. Firing on orgFilterLoading itself
  // (rather than defaultOrgIds?.length) means exactly one KPI fetch for the resolved default
  // scope, plus one more per genuine user org-selection change afterward.
  useEffect(() => {
    if (orgFilterLoading) return
    fetchKpi()
  }, [orgIds, isAdmin, orgFilterLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    DashboardService.fetchAnnouncement(
      (data) => { if (mountedRef.current) setAnnouncement(data || null) },
      (error) => { console.error('[Dashboard] Announcement fetch failed', error) }
    )
  }, [])

  return (
    <Box className={classes.root}>
      <AnnouncementBanner announcement={announcement} />
      <Box mt={3}>
        <Typography variant='h6' className={classes.sectionTitle}>
          Overview - Last 30 Days
        </Typography>
                        <Grid container spacing={2}>
          {metrics.map((metric) => (
            <Grid item xs={12} sm={6} md style={{ flexGrow: 1, flexBasis: 0 }} key={metric.label}>
              <MetricsCard
                label={metric.label}
                count={metric.count}
                trend={metric.trend}
                color={chartColors[metric.colorKey] || theme.palette.primary.main}
                description={metric.description}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box mt={3}>
        <FilingsByStatusChart
          orgIds={orgIds}
          setOrgIds={setOrgIds}
          canActAsOtherOrg={canActAsOtherOrg}
          orgList={orgList}
          isAdmin={isAdmin}
          defaultOrgIds={defaultOrgIds}
          orgFilterLoading={orgFilterLoading}
        />
      </Box>
    </Box>
  )
}

function DashboardPage(props) {
  const breadcrumbNameMap = useBreadcrumbNameMap()
  return (
    <CngBasePage
      renderPage={(showSnackbar) => (
        <DashboardContent showNotification={showSnackbar} {...props} />
      )}
      extraCngBreadcrumbNameMap={breadcrumbNameMap}
    />
  )
}

export default DashboardPage